import CheckIcon from "../../../assets/svg/v1/check-icon";
import { isColorLight } from "../utils/functions/get-is-color-light";
import { ColorPickerProps } from "../utils/types/types";

const ColorPicker = ({
  editor,
  onSetColor,
  onUnsetColor,
}: ColorPickerProps) => {
  const grayscale = [
    "#000000",
    "#434343",
    "#666666",
    "#999999",
    "#b7b7b7",
    "#cccccc",
    "#efefef",
  ];
  const palette = [
    "#ff0000",
    "#ff9900",
    "#ffff00",
    "#00ff00",
    "#00ffff",
    "#0000ff",
    "#9900ff",
    "#ff00ff",
  ];
  const tones = [
    [
      "#FADBD8",
      "#FAE5D3",
      "#FCF3CF",
      "#D4EFDF",
      "#D6EAF8",
      "#EBDEF0",
      "#EAECEE",
      "#D6DBDF",
    ],
    [
      "#F5B7B1",
      "#F5CBA7",
      "#F9E79F",
      "#A9DFBF",
      "#AED6F1",
      "#D2B4DE",
      "#D7DBDD",
      "#BFC9CA",
    ],
    [
      "#F1948A",
      "#F0B27A",
      "#F7DC6F",
      "#7DCEA0",
      "#85C1E9",
      "#BB8FCE",
      "#B2BABB",
      "#AAB7B8",
    ],
    [
      "#EC7063",
      "#EB984E",
      "#F4D03F",
      "#52BE80",
      "#5DADE2",
      "#A569BD",
      "#808B96",
      "#7F8C8D",
    ],
    [
      "#E74C3C",
      "#E67E22",
      "#F1C40F",
      "#27AE60",
      "#3498DB",
      "#8E44AD",
      "#566573",
      "#515A5A",
    ],
    [
      "#b03a2e",
      "#af601a",
      "#b7950b",
      "#1e8449",
      "#2874a6",
      "#6c3483",
      "#2e4053",
      "#212f3d",
    ],
  ];

  // Helper function to get current color from editor
  const getCurrentColor = (): string | null => {
    try {
      const colorAttr = editor.getAttributes("color");
      const textStyleAttr = editor.getAttributes("textStyle");
      return colorAttr?.color || textStyleAttr?.color || null;
    } catch {
      return null;
    }
  };

  const normalizeColor = (color: string | null): string | null => {
    if (!color) return null;
    return color.toLowerCase();
  };

  const colorsMatch = (
    color1: string | null,
    color2: string | null
  ): boolean => {
    return normalizeColor(color1) === normalizeColor(color2);
  };

  const ColorGrid = ({
    colors,
    onSelect,
    isActive,
    onClear,
    hasClear,
  }: {
    colors: string[];
    onSelect: (color: string) => void;
    isActive: (color: string | null) => boolean;
    onClear?: () => void;
    hasClear?: boolean;
  }) => {
    return (
      <div className="grid grid-cols-8 gap-1 w-[200px]">
        {hasClear && (
          <button
            onClick={onClear}
            className="w-4 h-4 rounded-sm border border-border-gray dark:border-gray-375 flex items-center justify-center bg-pure-white dark:bg-verba-indigo"
          >
            {isActive(null) && <CheckIcon className="w-4 h-4 text-black" />}
          </button>
        )}
        {colors.map((color) => (
          <button
            key={color}
            onClick={() => onSelect(color)}
            className="w-4 h-4 rounded-sm border border-border-gray dark:border-gray-375 hover:border-gray-375 dark:hover:border-gray-175 transition-colors"
            style={{ backgroundColor: color }}
          >
            {isActive(color) && (
              <CheckIcon
                className={`w-4 h-4 ${
                  isColorLight(color) ? "text-black" : "text-white"
                }`}
              />
            )}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="px-[12px] py-[10px]">
      <div>
        <p className="text-sm font-medium mb-2 text-verba-indigo dark:text-verba-indigo-dk">
          Text colour
        </p>
        <div className="space-y-1">
          <ColorGrid
            colors={grayscale}
            onSelect={onSetColor}
            isActive={(color: string | null) =>
              colorsMatch(getCurrentColor(), color)
            }
            onClear={onUnsetColor}
            hasClear
          />
          <ColorGrid
            colors={palette}
            onSelect={onSetColor}
            isActive={(color: string | null) =>
              colorsMatch(getCurrentColor(), color)
            }
          />
          {tones.map((row, i) => (
            <ColorGrid
              key={i}
              colors={row}
              onSelect={onSetColor}
              isActive={(color: string | null) =>
                colorsMatch(getCurrentColor(), color)
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ColorPicker;
